import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Shield, Users, UserCheck, Plus, RotateCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import AdminUserManagement from '@/components/AdminUserManagement';
import AdminCouponManagement from '@/components/AdminCouponManagement';

const Admin = () => {
  const { user, session } = useAuth();
  const { toast } = useToast();
  const [etsyApiCallCount, setEtsyApiCallCount] = useState<number | null>(null);

  console.log('=== ADMIN PAGE RENDER ===');
  console.log('User:', user?.email);
  console.log('Session valid:', !!session?.access_token);

  // Admin check - simplified since ProtectedRoute ensures we have user/session
  const { data: isAdmin, isLoading: checkingAdmin, error: adminError } = useQuery({
    queryKey: ['admin-check', user?.id],
    queryFn: async () => {
      console.log('=== CHECKING ADMIN STATUS ===');
      console.log('User ID:', user?.id);
      console.log('User email:', user?.email);
      
      try {
        // Try RPC function first
        const { data, error } = await supabase
          .rpc('is_admin', { user_uuid: user!.id });
        
        if (error) {
          console.error('RPC admin check failed:', error);
          
          // Fallback to direct query
          const { data: adminData, error: directError } = await supabase
            .from('admin_users')
            .select('user_id')
            .eq('user_id', user!.id)
            .maybeSingle();
          
          if (directError) {
            console.error('Direct admin query failed:', directError);
            return false;
          }
          
          const result = !!adminData;
          console.log('Admin check result (direct):', result);
          return result;
        }
        
        console.log('Admin check result (RPC):', data);
        return data;
      } catch (error) {
        console.error('Admin check error:', error);
        return false;
      }
    },
    enabled: !!user?.id,
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get admin stats
  const { data: stats } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const [usersResult, subscriptionsResult] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact' }),
        supabase.from('user_subscriptions').select('id, status', { count: 'exact' })
      ]);

      const activeSubscriptions = subscriptionsResult.data?.filter(
        sub => sub.status === 'active'
      ).length || 0;

      return {
        totalUsers: usersResult.count || 0,
        totalSubscriptions: subscriptionsResult.count || 0,
        activeSubscriptions,
      };
    },
    enabled: !!isAdmin,
  });

  const fetchEtsyApiCallCount = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('etsy-api-monitor');
      if (error) throw error;
      setEtsyApiCallCount(data.call_count ?? 0);
    } catch (err) {
      setEtsyApiCallCount(null);
    }
  };

  useEffect(() => {
    // Fetch Etsy API call count only if admin
    if (!isAdmin) return;
    fetchEtsyApiCallCount();
  }, [isAdmin]);

  const createSampleCoupons = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('create-sample-coupons');
      
      if (error) throw error;
      
      if (data.success) {
        toast({
          title: "Sample coupons created!",
          description: `Created ${data.coupons?.length || 0} sample coupons including TEST1, WELCOME10, SAVE20, and NEWUSER5.`,
        });
      }
    } catch (error: any) {
      console.error('Error creating sample coupons:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create sample coupons.",
        variant: "destructive",
      });
    }
  };

  const resetEtsyApiCallCount = async () => {
    try {
      // Call the reset function using the SQL approach
      const { error } = await supabase
        .from('etsy_api_monitor')
        .update({ call_count: 0 })
        .eq('id', 1);
      
      if (error) throw error;
      
      // Refresh the displayed count
      await fetchEtsyApiCallCount();
      
      toast({
        title: "Etsy API Calls Reset",
        description: "The Etsy API call counter has been reset to 0.",
      });
    } catch (error: any) {
      console.error('Error resetting Etsy API call count:', error);
      toast({
        title: "Error",
        description: "Failed to reset Etsy API call count.",
        variant: "destructive",
      });
    }
  };

  // Show loading while checking admin status
  if (checkingAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking admin access...</p>
          <p className="text-sm text-gray-500">User: {user?.email}</p>
        </div>
      </div>
    );
  }

  // Show access denied if not admin
  if (isAdmin === false) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Alert variant="destructive" className="max-w-md">
          <Shield className="h-4 w-4" />
          <AlertDescription>
            Access denied. You don't have admin privileges.
            <br />
            <span className="text-xs">Signed in as: {user?.email}</span>
            {adminError && (
              <div className="mt-2 text-xs">
                <p>Error: {adminError.message}</p>
              </div>
            )}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Show admin panel
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-8 w-8 text-purple-600" />
            Admin Panel
          </h1>
          <p className="text-gray-600 mt-2">Manage users and subscriptions</p>
          <p className="text-sm text-gray-500">Signed in as: {user?.email}</p>
          {isAdmin && (
            <div className="mt-4 flex items-center gap-2">
              <span className="inline-flex items-center px-2 py-1 rounded bg-orange-100 text-orange-700 text-xs font-semibold">
                Etsy API Calls: {etsyApiCallCount !== null ? etsyApiCallCount : '...'}
              </span>
              <Button
                onClick={resetEtsyApiCallCount}
                size="sm"
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Reset
              </Button>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalUsers || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.activeSubscriptions || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Subscriptions</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalSubscriptions || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <Button onClick={createSampleCoupons} className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Create Sample Coupons
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* User Management */}
        <div className="mb-8">
          <AdminUserManagement />
        </div>

        {/* Coupon Management */}
        <AdminCouponManagement />
      </div>
    </div>
  );
};

export default Admin;
